import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWalletStore } from '../store/walletStore';
import * as api from '../api/vexforge';
import toast from 'react-hot-toast';
import { signAndSend, ensureConnected } from '../lib/submit';
import {
  CommandLineIcon,
  DocumentTextIcon,
  CubeTransparentIcon,
  PaperAirplaneIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

const TABS = [
  { id: 'intent', name: 'Intent Terminal', icon: CommandLineIcon },
  { id: 'anchor', name: 'Anchor Data', icon: DocumentTextIcon },
  { id: 'tokens', name: 'My Tokens', icon: CubeTransparentIcon },
];

export default function Tools() {
  const { isConnected } = useWalletStore();
  const [activeTab, setActiveTab] = useState('intent');

  // Intent terminal state
  const [intentText, setIntentText] = useState('');
  const [intentResult, setIntentResult] = useState(null);
  const [submittingIntent, setSubmittingIntent] = useState(false);

  // Anchor state
  const [anchorNs, setAnchorNs] = useState('');
  const [anchorKey, setAnchorKey] = useState('');
  const [anchorHash, setAnchorHash] = useState('');
  const [anchorUri, setAnchorUri] = useState('');
  const [submittingAnchor, setSubmittingAnchor] = useState(false);

  // My tokens state
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);

  // Free text → read-only vex_parseIntent → structured goal → the user signs THAT
  // goal in VexSpark (which shows it on the approval screen). The old call here
  // was the unsigned vex_submitIntent, with its arguments in the wrong order.
  const handleSubmitIntent = async () => {
    const text = intentText.trim();
    if (!text) return;
    if (!isConnected) { await ensureConnected(); return; }
    setSubmittingIntent(true);
    setIntentResult(null);
    try {
      const parsed = await api.parseIntent(text);
      const hash = await signAndSend(api.intentOps(parsed, text), `Intent: ${text.slice(0, 120)}`);
      if (hash) {
        setIntentResult({ success: true, hash });
        toast.success('Intent submitted');
      }
    } catch (err) {
      setIntentResult({ success: false, error: err.message });
      toast.error(err.message);
    }
    setSubmittingIntent(false);
  };

  const handleAnchor = async () => {
    if (!anchorNs || !anchorKey || !anchorHash) return toast.error('Fill in namespace, key, and hash');
    if (!isConnected) { await ensureConnected(); return; }
    setSubmittingAnchor(true);
    try {
      const ops = api.anchorOps(anchorNs, api.anchorKeyHex(anchorKey), anchorHash, anchorUri || null);
      const hash = await signAndSend(ops, `Anchor data in "${anchorNs}"`);
      if (hash) {
        toast.success(`Anchored: ${hash}`);
        setAnchorKey('');
        setAnchorHash('');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setSubmittingAnchor(false);
  };

  const loadMyTokens = async () => {
    setLoadingTokens(true);
    try {
      const all = await api.listTokens(200);
      // Filter to tokens created by this address (approximation — creator field)
      setTokens(all || []);
    } catch {
      setTokens([]);
    }
    setLoadingTokens(false);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-3">Tools</h1>
        <p className="text-gray-400">Utilities for interacting with the Vextaris blockchain.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-800 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'tokens' && tokens.length === 0) loadMyTokens();
              }}
              className={`flex items-center space-x-2 px-4 py-2.5 font-medium whitespace-nowrap transition-colors text-sm ${
                activeTab === tab.id
                  ? 'text-white border-b-2 border-vextaris-purple'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          );
        })}
      </div>

      {/* Intent Terminal */}
      {activeTab === 'intent' && (
        <div className="max-w-2xl">
          <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Intent Terminal</h2>
            <p className="text-gray-500 text-sm mb-4">
              Type what you want to do in natural language. The IntentVM parses, plans, and executes atomically.
            </p>

            <div className="space-y-4">
              <textarea
                value={intentText}
                onChange={(e) => setIntentText(e.target.value)}
                placeholder="swap 10 USDC for VXS&#10;transfer 100 VXS to Vx0...&#10;stake 1000 VXS&#10;buy 5 SOL with 500 USDC"
                className="w-full px-4 py-3 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple h-28 resize-none font-mono text-sm"
              />

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSubmitIntent}
                  disabled={submittingIntent || !intentText.trim()}
                  className="inline-flex items-center space-x-2 px-5 py-2.5 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  <span>{submittingIntent ? 'Submitting...' : 'Execute Intent'}</span>
                </button>
                {!isConnected && <span className="text-gray-500 text-sm">Connect wallet to execute</span>}
              </div>

              {intentResult && (
                <div className={`p-4 rounded-lg border ${
                  intentResult.success
                    ? 'bg-green-500/5 border-green-500/20'
                    : 'bg-red-500/5 border-red-500/20'
                }`}>
                  {intentResult.success ? (
                    <div>
                      <p className="text-green-400 text-sm font-medium mb-1">Intent submitted</p>
                      <p className="text-gray-400 text-xs font-mono">{intentResult.hash}</p>
                      <a href={`https://vexscan.io/tx/${intentResult.hash}`} target="_blank" rel="noopener noreferrer"
                        className="text-vextaris-blue text-xs hover:underline">View on VexScan</a>
                    </div>
                  ) : (
                    <p className="text-red-400 text-sm">{intentResult.error}</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-gray-600 text-xs">
                Supports: swap, buy, sell, transfer, send, stake, bridge, register VNS names, and multi-input composites.
                Constraints: "max fee 50000", "deadline 5 minutes", "slippage 2%".
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Anchor Data */}
      {activeTab === 'anchor' && (
        <div className="max-w-2xl">
          <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Anchor Data</h2>
            <p className="text-gray-500 text-sm mb-4">
              Write a hash to the blockchain as tamper-evident proof. 5,000 gas per anchor.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Namespace</label>
                <input type="text" value={anchorNs} onChange={(e) => setAnchorNs(e.target.value)}
                  placeholder="myapp" className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Key</label>
                <input type="text" value={anchorKey} onChange={(e) => setAnchorKey(e.target.value)}
                  placeholder="document-id or hex bytes" className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">BLAKE3 Hash (Vxh or 0x)</label>
                <input type="text" value={anchorHash} onChange={(e) => setAnchorHash(e.target.value)}
                  placeholder="Vxh... or 0x..." className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Metadata URI (optional)</label>
                <input type="text" value={anchorUri} onChange={(e) => setAnchorUri(e.target.value)}
                  placeholder="ipfs://Qm... or https://..." className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple" />
              </div>

              <button
                onClick={handleAnchor}
                disabled={submittingAnchor || !anchorNs || !anchorKey || !anchorHash}
                className="inline-flex items-center space-x-2 px-5 py-2.5 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <DocumentTextIcon className="w-4 h-4" />
                <span>{submittingAnchor ? 'Anchoring...' : 'Anchor on Chain'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Tokens */}
      {activeTab === 'tokens' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Token Registry</h2>
            <button onClick={loadMyTokens} className="text-sm text-gray-500 hover:text-white transition-colors">
              Refresh
            </button>
          </div>

          {loadingTokens ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vextaris-purple mx-auto"></div>
            </div>
          ) : tokens.length === 0 ? (
            <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-500">No tokens found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tokens.slice(0, 50).map((t, i) => (
                <Link
                  key={i}
                  to={`/token/${t.mint_address || t.mint_address_hex || ''}`}
                  className="flex items-center justify-between bg-vextaris-darker border border-gray-800 rounded-lg p-4 hover:border-vextaris-purple/30 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {t.image ? (
                      <img src={t.image} alt={t.symbol} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400">
                        {(t.symbol || '?').charAt(0)}
                      </div>
                    )}
                    <div>
                      <span className="text-white font-medium">{t.name}</span>
                      <span className="text-gray-500 ml-2">{t.symbol}</span>
                    </div>
                  </div>
                  <span className="text-gray-600 text-xs">{t.standard || 'VSC-7'}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
